import React, { useCallback, useMemo, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { admin_cse } from "@/config/roles";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { hasAccessToPaymentTab } from "@/utils/authUtils";
import { isAuthorized } from "@/utils/authToken";
import MemberProfileAdmin from "@/components/Provider/PatientView/MemberProfile/MemberProfileAdmin";
import { MemberProfileCarePlan } from "@/features/member-profile/(care-plan)/MemberProfileCarePlan";
import { MemberProfileReports } from "@/components/Provider/PatientView/MemberProfile/Reports/MemberProfileReports";
import { patientKeys } from "@/features/patient/api/keyFactory";
import { Payment } from "@/components/Provider/PatientView/MemberProfile/Payment/Payment";
import { usePatientDetails } from "@/features/patient/api/queries/usePatientDetails";
import type { UUID } from "@/core/types";
import { ViewContent2 } from "@/components/layout/ViewContent";

import { EligibilityAlertBanner } from "./components/EligibilityAlertBanner";
import { MemberProfileHeader } from "./(header)/MemberProfileHeader";
import { MemberProfileMedical } from "./(medical)/MemberProfileMedical";
import { MemberProfileNotes } from "./(notes)/MemberProfileNotes";
import { MemberProfileTimeline } from "./(timeline)/MemberProfileTimeline";
import { PAYMENT_TAB } from "./constants";
import { PaymentAlertBanner } from "./components/PaymentAlertBanner";
import { SchedulingRestrictedBanner } from "./components/SchedulingRestrictedBanner";

import "./MemberProfile.styles.scss";

export function MemberProfile() {
  const { patientId } = useParams<{ patientId: UUID }>();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: patientDetails } = usePatientDetails({ patientId });

  const adminFocusRef = useRef<HTMLElement>(null);
  // memoized — isAuthorized() reads a JWT on every call
  // Admin tab is admin_cse-only (defense against admin_csm reaching it via direct/deep link)
  const isAdminCse = useMemo(() => isAuthorized(admin_cse), []);

  // URLSearchParams.get() correctly decodes "+" as space (form-encoding)
  const selectedTab = new URLSearchParams(location.search).get("selectedTab") || "Care Plan";

  const refetchPatientDetails = useCallback(
    () => queryClient.invalidateQueries({ queryKey: patientKeys.details(patientId) }),
    [queryClient, patientId],
  );

  const selectedTabChangeHandler = useCallback(
    (tab: string) => {
      const searchParams = new URLSearchParams(history.location.search);

      searchParams.set("selectedTab", tab);
      history.push(`${history.location.pathname}?${searchParams.toString()}`);
    },
    [history],
  );

  if (!patientDetails) return null;

  const paywallStatus = patientDetails?.paywall_status;
  const patientName = patientDetails?.preferred_name || patientDetails.first_name;
  const automaticPaymentEnabled = patientDetails?.automatic_payment_enabled;
  const isSchedulingRestricted = patientDetails?.is_scheduling_restricted;

  return (
    <ViewContent2 {...getAutomationDataAttr("MemberProfilePage")}>
      <PaymentAlertBanner
        automaticPaymentEnabled={automaticPaymentEnabled}
        patientName={patientName}
        paywallStatus={paywallStatus}
        softPaywallTimestamp={patientDetails?.soft_paywall}
        {...getAutomationDataAttr("patient-profile-payment-alert-banner")}
      />

      <SchedulingRestrictedBanner
        isAdmin={isAdminCse}
        isSchedulingRestricted={!paywallStatus?.paywall && isSchedulingRestricted}
        onNavigateToAdmin={() => {
          selectedTabChangeHandler("Admin");
          setTimeout(() => adminFocusRef?.current?.scrollIntoView(), 0);
        }}
        {...getAutomationDataAttr("patient-profile-scheduling-restricted-banner")}
      />

      <EligibilityAlertBanner
        eligibilityStatus={patientDetails?.primary_eligibility_record?.status}
        firstName={patientDetails.first_name}
        {...getAutomationDataAttr("patient-profile-eligibility-alert-banner")}
      />

      <MemberProfileHeader />

      {selectedTab === "Reports" && <MemberProfileReports />}

      {selectedTab === "Care Plan" && <MemberProfileCarePlan patientDetails={patientDetails} />}

      {selectedTab === "Notes" && <MemberProfileNotes />}

      {selectedTab === "Medical" && (
        <MemberProfileMedical
          patientDetails={patientDetails}
          refetchPatientDetails={refetchPatientDetails}
        />
      )}

      {selectedTab === "Timeline" && (
        <MemberProfileTimeline
          patientDetails={patientDetails}
          refetchPatientDetails={refetchPatientDetails}
        />
      )}

      {hasAccessToPaymentTab() && selectedTab === PAYMENT_TAB && (
        <Payment patientDetails={patientDetails} />
      )}

      {isAdminCse && selectedTab === "Admin" && (
        <MemberProfileAdmin
          adminFocusRef={adminFocusRef}
          patientDetails={patientDetails}
          refetchPatientDetails={refetchPatientDetails}
          selectedTabChangeHandler={selectedTabChangeHandler}
        />
      )}
    </ViewContent2>
  );
}
